import React, { useCallback, useEffect, useState, useMemo } from "react";
import Form from "./Form";
import SpecsListWithoutGroups from "./Popular";
import SpecsList from "./SpecsList";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Selectors as SearchSelectors } from "@/store/masterSearch/selectors";
import { useParsedQuery } from "@/hooks/useParsedQuery";
import { Container } from "reactstrap";
import { matchPath, useLocation, useHistory } from "react-router-dom";
import { SEARCH_NS } from "@/i18n/ns";
import { preloadNS } from "@/i18n/utils";
import { preloadModules } from "@/utils/loadable";
import SearchPageLoadable from "_p/search/loadable";
import { useAllSpecsAndGroupsQuery } from "@/hooks/queries/useAllSpecsAndGroupsQuery";
import isempty from "lodash/isEmpty";
import QueryRequest from "../QueryRequest";
import ErrorBoundary from "../ErrorBoundary";

const Search = ({ className, coords, placeName }) => {
  const history = useHistory();
  const specsQuery = useAllSpecsAndGroupsQuery();
  const specsQueryData = specsQuery.data;
  const hasGroups = !isempty(specsQueryData?.groups);
  const specsArrayIfThereAreNoGroups = useMemo(() => {
    const specsMap = specsQueryData?.specs;
    if (hasGroups || isempty(specsMap)) return [];
    return Object.entries(specsMap)
      .map(([_id, data]) => ({ _id, ...data }))
      .filter((spec) => spec.searchable);
  }, [specsQueryData, hasGroups]);
  const groupedSpecsByGroup = useMemo(() => {
    if (!hasGroups) return [];
    const groupsMap = specsQueryData.groups;
    const specsMap = specsQueryData.specs;

    const grouped = {};
    for (const specId in specsMap) {
      const spec = specsMap[specId];
      const groupId = spec.group;
      if (!groupId) {
        continue;
      }
      const group = groupsMap[groupId];
      if (!group) {
        continue;
      }
      if (spec.searchable && group.searchable) {
        if (!grouped[groupId]) {
          grouped[groupId] = {
            group,
            specs: [],
          };
        }
        grouped[groupId].specs.push({
          _id: specId,
          ...spec,
        });
      }
    }
    return grouped;
  }, [specsQueryData, hasGroups]);
  const preloadSearchPage = useCallback(() => {
    preloadNS([SEARCH_NS]);
    preloadModules([SearchPageLoadable]);
  }, []);
  const { pathname } = useLocation();
  const matchedPath = useMemo(() => {
    return matchPath(pathname, { path: "/search/:loc/:spec?" });
  }, [pathname]);
  const [manuallyChangedLocation, setManuallyChangedLocation] = useState(null);
  const { t, ready } = useTranslation(["common", "errors"], {
    useSuspense: false,
  });
  const parsedQuery = useParsedQuery();
  useEffect(() => {
    setManuallyChangedLocation(null);
  }, [parsedQuery, matchedPath]);
  const SpecsComponent = hasGroups ? SpecsList : SpecsListWithoutGroups;
  const specsProp = hasGroups
    ? groupedSpecsByGroup
    : specsArrayIfThereAreNoGroups;
  if (!ready) return null;
  return (
    <Container className={className}>
      <Form
        matchedPath={matchedPath}
        coords={coords}
        preloadSearchPage={preloadSearchPage}
        onLocationChange={setManuallyChangedLocation}
        name={placeName}
        t={t}
        query={parsedQuery}
      />
      <ErrorBoundary
        onReset={() => {
          history.push("/");
        }}
      >
        <QueryRequest query={specsQuery}>
          {!isempty(specsProp) && (
            <>
              <SpecsComponent
                matchedPath={matchedPath}
                query={parsedQuery}
                t={t}
                manualLocation={manuallyChangedLocation}
                specs={specsProp}
              />
            </>
          )}
        </QueryRequest>
      </ErrorBoundary>
    </Container>
  );
};

export default connect((state) => ({
  coords: SearchSelectors.getLocCoords(state),
  hasCoords: SearchSelectors.hasCoords(state),
  placeName: SearchSelectors.getLocName(state),
}))(Search);
